/**
 * What this user has already celebrated, per device. localStorage because a
 * repeat on a second device is harmless and a server column would need a
 * migration for a cosmetic. The watermark is set on FIRST run so a member with
 * history doesn't get a burst for every past event the day this ships.
 * Every storage touch is guarded; the in-memory copy keeps dedupe working in
 * private mode.
 */
export interface SeenState {
  watermark: string;
  keys: string[];
}

const PREFIX = "wexare-celebrations:";
export const MAX_SEEN_KEYS = 200;
const memory = new Map<string, SeenState>();

function read(userId: string): SeenState | null {
  const cached = memory.get(userId);
  if (cached) return cached;
  try {
    const raw = localStorage.getItem(PREFIX + userId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SeenState>;
    if (typeof parsed.watermark !== "string" || !Array.isArray(parsed.keys)) return null;
    const state = {
      watermark: parsed.watermark,
      keys: parsed.keys.filter((k): k is string => typeof k === "string"),
    };
    memory.set(userId, state);
    return state;
  } catch {
    return null;
  }
}

function write(userId: string, state: SeenState): void {
  memory.set(userId, state);
  try {
    localStorage.setItem(PREFIX + userId, JSON.stringify(state));
  } catch {
    /* private mode / quota — memory copy still dedupes this session */
  }
}

export function loadSeen(userId: string, now: Date = new Date()): SeenState {
  const existing = read(userId);
  if (existing) return existing;
  const fresh = { watermark: now.toISOString(), keys: [] };
  write(userId, fresh);
  return fresh;
}

export function markSeen(userId: string, keys: string[]): void {
  const state = loadSeen(userId);
  const known = new Set(state.keys);
  const added = keys.filter((k) => !known.has(k) && known.add(k));
  if (added.length === 0) return;
  write(userId, {
    watermark: state.watermark,
    keys: [...state.keys, ...added].slice(-MAX_SEEN_KEYS),
  });
}

export function __resetSeenMemoryForTests(): void {
  memory.clear();
}
